import { Injectable } from '@angular/core';
import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { AccountProductsService } from '@epicuro-next/data/account-products-data';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { selectUserLoggedId } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from './customer-product-identifiers.actions';
import { selectLoaded } from './customer-product-identifiers.selectors';

@Injectable()
export class CustomerProductIdentifiersEffects implements OnInitEffects {
  public init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.initModule),
      switchMap(() =>
        this.store.pipe(select(selectUserLoggedId), filter(Boolean), take(1)),
      ),
      switchMap(() =>
        this.store.pipe(
          select(
            selectConfigurationValueFrom<
              PulseConfigurationKeys.CustomerProductIdentifiersEnabled,
              PulseConfigurations
            >(
              PULSE_FEATURE_NAME,
              PulseConfigurationKeys.CustomerProductIdentifiersEnabled,
              false,
            ),
          ),
          filter(Boolean),
          take(1),
        ),
      ),
      map(() => actions.loadCustomerProductIdentifiers()),
    ),
  );

  public loadCustomerProductIdentifiersOnAccountChange$ = createEffect(() => {
    return this.store.pipe(
      select(selectLoaded),
      filter((loaded) => loaded),
      take(1),
      switchMap(() => {
        return this.store.pipe(
          select(selectActiveAccountId),
          filter(isNonNullish),
          distinctUntilChanged(),
          map(() => actions.loadCustomerProductIdentifiers()),
        );
      }),
    );
  });

  public loadCustomerProductIdentifiers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadCustomerProductIdentifiers),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
      ),
      exhaustMap(([, activeAccountId]) =>
        this.accountProductsService.getAccountProducts(activeAccountId).pipe(
          map((identifiers) =>
            actions.loadCustomerProductIdentifiersSuccess({
              identifiers,
            }),
          ),
          catchError((error) =>
            of(actions.loadCustomerProductIdentifiersFailure({ error })),
          ),
        ),
      ),
    ),
  );

  public updateCustomerProductIdentifiers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateCustomerProductIdentifier),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
      ),
      concatMap(
        ([
          { brandId, productId, customerProductIdentifier },
          activeAccountId,
        ]) =>
          this.accountProductsService
            .putAccountProducts(
              activeAccountId,
              brandId,
              productId,
              customerProductIdentifier,
            )
            .pipe(
              switchMap(() => [
                actions.updateCustomerProductIdentifierSuccess(),
                actions.loadCustomerProductIdentifiers(),
              ]),
              catchError((error) =>
                of(actions.updateCustomerProductIdentifierFailure({ error })),
              ),
            ),
      ),
    ),
  );

  public ngrxOnInitEffects() {
    return actions.initModule();
  }

  constructor(
    private actions$: Actions,
    private accountProductsService: AccountProductsService,
    private store: Store,
  ) {}
}
